import { DOCUMENT }                      from '@angular/common';
import {
  Inject,
  Injectable,
}                                        from '@angular/core';
import { MatDialog }                     from '@angular/material/dialog';
import {
  ActivatedRoute,
  Router,
}                                        from '@angular/router';
import { AuthService }                   from '@auth0/auth0-angular';
import {
  Actions,
  createEffect,
  ofType,
}                                        from '@ngrx/effects';
import { routerNavigatedAction }         from '@ngrx/router-store';
import { Store }                         from '@ngrx/store';
import { TranslateService }              from '@ngx-translate/core';
import {
  catchError,
  concatMap,
  filter,
  forkJoin,
  map,
  of,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
}                                        from 'rxjs';
import {
  BeneficiaryType,
  Feature,
  mimeType,
}                                        from '~domain/enums';
import {
  Beneficiary,
  CreditorRemittanceInformationType,
  displayDocumentType,
  payDocumentType,
  PaymentFormData,
  PredictClientResponse,
  SimpleClient,
  toBeneficiaryFormData,
}                                        from '~domain/types';
import { AddClientApiService }           from '../add-client/add-client-api.service';
import {
  ENVIRONMENT,
  LOCAL_STORAGE,
}                                        from '../app.constants';
import { AppRoute }                      from '../app.route.enum';
import {
  CounterApiService,
  FeatureFlagApiService,
  FeatureService,
  OfficeApiService,
  RbAuthService,
  Toast,
  ToastService,
  UserApiService,
}                                        from '../core';
import {
  AssignDocumentDialogComponent,
  AssignDocumentRequest,
}                                        from '../document/assign-document/assign-document-dialog.component';
import { DeleteDocumentDialogComponent } from '../document/delete-document/delete-document-dialog.component';
import { DocumentApiService }            from '../document/document-api.service';
import {
  ProcessDocumentDialogComponent,
  ProcessDocumentRequest,
}                                        from '../document/process-document/process-document-dialog.component';
import { RenameDocumentDialogComponent } from '../document/rename-document/rename-document-dialog.component';
import {
  ViewDocumentDialogComponent,
  ViewDocumentRequest,
}                                        from '../document/view-document/view-document-dialog.component';
import { IsabelApiService }              from '../isabel/isabel-api.service';
import { IsabelRedirectDialogComponent } from '../isabel/isabel-redirect-dialog.component';
import { MaintenanceModeApiService }     from '../maintenance-mode/maintenance-mode-api.service';
import { PaymentApiService }             from '../payment/payment-api.service';
import {
  AddPaymentRequest,
  PaymentDialogComponent,
}                                        from '../payment/payment-dialog.component';
import { RespondToDocumentApiService }   from '../respond-to-document/respond-to-document-api.service';
import { Xs2aApiService }                from '../xs2a/xs2a-api.service';
import { Xs2aDialogComponent }           from '../xs2a/xs2a-dialog.component';
import { Xs2aRedirectDialogComponent }   from '../xs2a/xs2a-redirect-dialog.component';
import * as AppActions                   from './app.actions';
import * as AppSelectors                 from './app.selectors';

@Injectable()
export class AppEffects {
  processRouterLoginEvent$ = createEffect(() =>
      this.actions$.pipe(
        ofType(routerNavigatedAction),
        map((action) => action.payload.routerState),
        filter((state) => state.url.matchesUrl([AppRoute.Login])),
        map((state) => ({
          officeId: (state.root.queryParams['officeId'] ?? undefined) as string | undefined,
          invitation: (state.root.queryParams['invitation'] ?? undefined) as string | undefined,
          organization: (state.root.queryParams['organization'] ?? undefined) as string | undefined,
        })),
        tap(({ officeId, invitation, organization }) => this.rbAuthService.isAuthenticated$
          .pipe(take(1), withLatestFrom(this.rbAuthService.error$.pipe(startWith(null))))
          .subscribe(([isAuthenticated, error]) => {
            if (error || (isAuthenticated && !officeId)) {
              return;
            }
            if (invitation && organization) {
              this.rbAuthService.acceptInvitation(invitation, organization);
            } else {
              this.rbAuthService.login('/', true, officeId);
            }
          }),
        ),
      ),
    { dispatch: false },
  );

  logout$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppActions.logout),
        withLatestFrom(this.store.select(AppSelectors.selectAccountUser)),
        tap(([, user]) => {
          const officeId = user?.customerCareAccessActive ? user.officeId : undefined;
          this.rbAuthService.logout(officeId);
        }),
      ),
    { dispatch: false },
  );

  fetchColorMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchColorMode),
      map(() => AppActions.fetchColorModeResult({
        colorMode: this.localStorage.getItem('colorMode') as 'dark' | 'light' | null,
      })),
    ),
  );

  setColorMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.setColorMode),
      map(({ colorMode }) => {
        if (colorMode) this.localStorage.setItem('colorMode', colorMode);
        else this.localStorage.removeItem('colorMode');
        return AppActions.setColorModeResult({ colorMode });
      }),
    ),
  );

  fetchMaintenanceMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchMaintenanceMode),
      switchMap(() => this.maintenanceModeApiService.fetchMaintenanceModes()),
      map((maintenanceModes) => AppActions.fetchMaintenanceModeResult({
        maintenanceMode: maintenanceModes.global ?? false,
      })),
    ),
  );

  fetchAccountUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchAccountUser),
      withLatestFrom(this.authService.user$),
      switchMap(([, auth0User]) => this.userApiService.fetchAccountUser().pipe(
        switchMap(user => {
          if (user.invitationPending && auth0User?.sub) {
            return this.userApiService.updateAuth0UserId(auth0User.sub).pipe(map(() => user));
          }
          return of(user);
        }),
      )),
      tap(({ language }) => this.translateService.use(language)),
      map(user => AppActions.fetchAccountUserResult({ user })),
    ),
  );

  fetchUserSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchUserSettings),
      switchMap(() => this.userApiService.fetchUserSettings()),
      map(userSettings => AppActions.fetchUserSettingsResult({ userSettings })),
    ),
  );

  setUserSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.setUserSettings),
      withLatestFrom(this.store.select(AppSelectors.selectUserSettings)),
      concatMap(([{ userSettings }, currentSettings]) => this.userApiService.setUserSettings({ ...currentSettings, ...userSettings })),
      map(userSettings => AppActions.setUserSettingsResult({ userSettings })),
    ),
  );

  fetchClientBadgeCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchClientBadgeCount, AppActions.respondToDocumentResult),
      switchMap(() => this.counterApiService.getNumberOfClientsWithNotifications()),
      map(count => AppActions.fetchClientBadgeCountResult({ count })),
    ),
  );

  fetchNotificationBadgeCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchNotificationBadgeCount, AppActions.respondToDocumentResult),
      switchMap(() => this.counterApiService.getNumberOfOpenNotifications()),
      map(count => AppActions.fetchNotificationBadgeCountResult({ count })),
    ),
  );

  fetchPaymentBadgeCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchPaymentBadgeCount, AppActions.addPaymentResult),
      switchMap(() => this.counterApiService.getNumberOfQueuedPayments()),
      map(count => AppActions.fetchPaymentBadgeCountResult({ count })),
    ),
  );

  fetchUnlinkedAccountsBadgeCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchUnlinkedAccountsBadgeCount, AppActions.createAiarAuthorizationResult, AppActions.createIsabelAuthorizationSuccess),
      switchMap(() => this.counterApiService.getNumberOfUnlinkedAccounts()),
      map(count => AppActions.fetchUnlinkedAccountsBadgeCountResult({ count })),
    ),
  );

  fetchAccountsWithWarningBadgeCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchAccountsWithWarningBadgeCount),
      switchMap(() => this.counterApiService.getNumberOfAccountsWithWarning()),
      map(count => AppActions.fetchAccountsWithWarningBadgeCountResult({ count })),
    ),
  );

  fetchDocumentBadgeCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AppActions.fetchDocumentBadgeCount,
        AppActions.addPaymentResult,
        AppActions.assignDocumentSuccess,
        AppActions.deleteDocumentSuccess,
      ),
      switchMap(() => this.counterApiService.getNumberOfUnprocessedDocuments()),
      map(count => AppActions.fetchDocumentBadgeCountResult({ count })),
    ),
  );

  fetchOffice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchAccountUserResult),
      switchMap(({ user }) => this.officeApiService.fetchOffice(user.officeId)),
      map(office => AppActions.fetchOfficeResult({ office })),
    ),
  );

  fetchNewBudgetAppEnabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchNewBudgetAppEnabled),
      switchMap(() => this.featureFlagApiService.fetchNewBudgetAppEnabled().pipe(
        map(enabled => AppActions.fetchNewBudgetAppEnabledResult({ enabled })),
        catchError(() => of(AppActions.fetchNewBudgetAppEnabledError())),
      )),
    ),
  );

  closeDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.closeDialog),
      tap(({ message }) => {
        this.matDialog.closeAll();
        if (message) {
          this.toastService.show(Toast.Success, message);
        }
      }),
    ), { dispatch: false });

  createClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.createClient),
      concatMap(({ client }) =>
        this.addClientApiService.createClient(client).pipe(
          map(id => AppActions.createClientResult({ id, accountId: client.initialAccountId })),
          catchError(() => of(AppActions.createClientError())),
        ),
      ),
    ),
  );

  closeCreateClientDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.createClientResult),
      map(() => AppActions.closeDialog({ message: 'ADD_CLIENT' })),
    ),
  );

  navigateAfterCreatedClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.createClientResult),
      tap(({ id, accountId }) =>
        this.router.navigate(
          [accountId ? '/bank-accounts/' + accountId : '/clients/' + id],
          { onSameUrlNavigation: 'reload' },
        ),
      ),
    ), { dispatch: false });

  downloadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.downloadDocument),
      concatMap(({ id, clientId, name, extension }) => this.documentApiService.downloadDocument(id, clientId)
        .pipe(
          map((file) => {
            const objectURL = window.URL.createObjectURL(
              new Blob([file], { type: mimeType(extension) }),
            );
            const downloadAnchorNode = this.document.createElement('a');
            downloadAnchorNode.setAttribute('href', objectURL);
            downloadAnchorNode.setAttribute('download', `${name}.${extension}`);
            this.document.body.appendChild(downloadAnchorNode); // required for firefox
            downloadAnchorNode.click();
            downloadAnchorNode.remove();
            this.toastService.show(Toast.Success, 'DOCUMENT_DOWNLOADED');
            return AppActions.downloadDocumentResult();
          }),
          catchError(() => of(AppActions.downloadDocumentError())),
        ),
      ),
    ),
  );

  openRenameDocumentDialog$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppActions.openRenameDocumentDialog),
        tap(({ document }) => {
          const dialogRef = this.matDialog.openDialogs.length > 0 ? this.matDialog.openDialogs[0] : null;
          this.matDialog.open(RenameDocumentDialogComponent, { disableClose: true, data: document });
          dialogRef?.close();
        }),
      ),
    { dispatch: false },
  );

  renameDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.renameDocument),
      concatMap(({ name, documentId, clientId, closeDialog = true, reload = true }) =>
        this.documentApiService.renameDocument(name, documentId, clientId).pipe(
          switchMap(() => of(
            AppActions.renameDocumentSuccess(),
            ...(closeDialog ? [AppActions.closeDialog({ message: 'DOCUMENT_RENAMED' })] : []),
            ...(reload ? [AppActions.reloadPage()] : []),
          )),
          catchError(() => of(AppActions.renameDocumentError())),
        ),
      ),
    ),
  );

  setDocumentHidden$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.setDocumentHidden),
      concatMap(({ documentId, clientId, hidden }) =>
        this.documentApiService.setDocumentHidden(hidden, documentId, clientId).pipe(
          map(() => {
            this.toastService.show(Toast.Success, 'DOCUMENT_UPDATED');
            (this.matDialog.openDialogs.length > 0 ? this.matDialog.openDialogs[0] : null)?.close();
            return AppActions.setDocumentHiddenSuccess();
          }),
          catchError(() => of(AppActions.setDocumentHiddenError())),
        ),
      ),
    ),
  );

  setDocumentShared$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.setDocumentShared),
      concatMap(({ documentId, clientId, shared }) =>
        this.documentApiService.setDocumentShared(shared, documentId, clientId).pipe(
          map(() => {
            this.toastService.show(Toast.Success, 'DOCUMENT_UPDATED');
            (this.matDialog.openDialogs.length > 0 ? this.matDialog.openDialogs[0] : null)?.close();
            return AppActions.setDocumentSharedSuccess();
          }),
          catchError(() => of(AppActions.setDocumentSharedError())),
        ),
      ),
    ),
  );

  openDeleteDocumentDialog$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppActions.openDeleteDocumentDialog),
        tap(({ documents }) => {
          const dialogRef = this.matDialog.openDialogs.length > 0 ? this.matDialog.openDialogs[0] : null;
          this.matDialog.open(DeleteDocumentDialogComponent, { disableClose: true, data: { documents } });
          dialogRef?.close();
        }),
      ),
    { dispatch: false },
  );

  deleteDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.deleteDocument),
      concatMap(({ documentIds, queuedDocuments }) =>
        this.documentApiService.deleteDocuments(documentIds).pipe(
          map(() => AppActions.deleteDocumentSuccess({ queuedDocuments })),
          catchError(() => of(AppActions.deleteDocumentError())),
        ),
      ),
    ),
  );

  closeDeleteDocumentDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.deleteDocumentSuccess),
      filter(({ queuedDocuments }) => (queuedDocuments?.length ?? 0) === 0),
      map(() => AppActions.closeDialog({ message: 'DOCUMENT_DELETED' })),
    ),
  );

  nextQueuedDocumentAfterDeleteDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.deleteDocumentSuccess),
      filter(({ queuedDocuments }) => (queuedDocuments?.length ?? 0) > 0),
      tap(() => this.toastService.show(Toast.Success, 'DOCUMENT_DELETED')),
      map(({ queuedDocuments }) => {
        const qDocs = [...queuedDocuments!];
        const document = qDocs.shift()!;
        const documentType = payDocumentType(document);
        return AppActions.fetchDocumentProcessingData({
          document,
          documentType,
          queuedDocuments: qDocs,
        });
      }),
    ),
  );

  reloadAfterDocumentAction$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          AppActions.assignDocumentSuccess,
          AppActions.deleteDocumentSuccess,
          AppActions.setDocumentHiddenSuccess,
          AppActions.setDocumentSharedSuccess,
        ),
        tap(() => {
          this.router.navigate([], {
            relativeTo: this.route,
            replaceUrl: true,
            queryParamsHandling: 'merge',
            onSameUrlNavigation: 'reload',
          });
        }),
      ),
    { dispatch: false },
  );

  respondToDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.respondToDocument),
      concatMap(({ id, clientId, response, message }) =>
        this.respondToDocumentApiService.respondToDocument(id, response, clientId, message)
          .pipe(
            map(() => AppActions.respondToDocumentResult()),
            catchError(() => of(AppActions.respondToDocumentError())),
          ),
      ),
    ),
  );

  closeRespondToDocumentDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.respondToDocumentResult),
      map(() => AppActions.closeDialog({ message: 'DOCUMENT_RESPONSE_ADDED' })),
    ),
  );

  fetchDocumentPaymentData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchDocumentPaymentData),
      switchMap(({
                   clientId,
                   documentId,
                   documentName,
                   documentExtension,
                   documentType,
                   queuedDocuments,
                   paymentData,
                   reloadAfterSave,
                 }) => forkJoin([
        paymentData ? of(paymentData) : this.documentApiService.getDocumentPaymentData(documentId),
        this.documentApiService.downloadDocument(documentId, clientId),
      ]).pipe(
        map(([paymentData, blob]) => AppActions.fetchDocumentPaymentDataResult({
          documentName,
          documentExtension,
          documentType,
          documentUrl: URL.createObjectURL(blob),
          queuedDocuments,
          paymentData: { ...paymentData, clientId, documentId },
          reloadAfterSave,
        })),
        catchError(() => of(AppActions.fetchDocumentPaymentDataError())),
      )),
    ),
  );

  fetchInfoDocumentPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchDocumentPaymentDataResult),
      switchMap(({
                   documentName,
                   documentExtension,
                   documentType,
                   documentUrl,
                   queuedDocuments,
                   paymentData,
                   reloadAfterSave,
                 }) => {
        if (paymentData.documentId && !paymentData.clientId) {
          return this.documentApiService.predictClient(paymentData.documentId)
            .pipe(catchError(() => of({} as PredictClientResponse)))
            .pipe(map((predictedClientResponse) => {
              return AppActions.fetchInfoPaymentDialog({
                documentName,
                documentExtension,
                documentType,
                documentUrl,
                queuedDocuments,
                paymentData,
                reloadAfterSave,
                predictedClient: predictedClientResponse.predictedClient,
              });
            }));
        }
        return of(AppActions.fetchInfoPaymentDialog({
          documentName,
          documentExtension,
          documentType,
          documentUrl,
          queuedDocuments,
          paymentData,
          reloadAfterSave,
        }));
      }),
    ),
  );

  fetchInfoPaymentDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchInfoPaymentDialog),
      withLatestFrom(
        this.store.select(AppSelectors.selectPaymentAccounts),
        this.store.select(AppSelectors.selectPaymentClients),
        this.store.select(AppSelectors.selectAccountUser).pipe(filter(Boolean)),
      ),
      switchMap(([
                   {
                     documentName,
                     documentExtension,
                     documentType,
                     documentUrl,
                     queuedDocuments,
                     paymentData,
                     reloadAfterSave,
                     predictedClient,
                   },
                   paymentAccounts, paymentClients, user,
                 ]) => forkJoin(([
          paymentAccounts.length > 0 ? of(undefined) : this.paymentApiService.fetchAccounts(),
          paymentClients.length > 0 ? of(undefined) : this.paymentApiService.fetchClients(),
          this.paymentApiService.fetchGlobalBeneficiaries(user.officeId),
          paymentData.clientId ? this.paymentApiService.fetchClientBeneficiaries(paymentData.clientId) : of([]),
        ])).pipe(
          map(([paymentAccounts, paymentClients, globalBeneficiaries, clientBeneficiaries]) => AppActions.fetchInfoPaymentDialogResult({
            documentName,
            documentExtension,
            documentType,
            documentUrl,
            queuedDocuments,
            paymentData,
            paymentAccounts,
            paymentClients,
            globalBeneficiaries,
            clientBeneficiaries,
            reloadAfterSave,
            predictedClient,
          })),
          catchError(() => of(AppActions.fetchInfoPaymentDialogError())),
        ),
      ),
    ),
  );

  fetchPaymentAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchInfoPaymentDialog, AppActions.fetchDocumentProcessingDataResult),
      withLatestFrom(this.store.select(AppSelectors.selectPaymentAccounts)),
      filter(([, paymentAccountsFromStore]) => paymentAccountsFromStore.length > 0),
      switchMap(() => this.paymentApiService.fetchAccounts().pipe(
        map(paymentAccounts => AppActions.fetchPaymentAccountsResult({ paymentAccounts })),
      )),
    ),
  );

  fetchPaymentClients$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AppActions.fetchPaymentClients,
        AppActions.fetchInfoPaymentDialog,
        AppActions.fetchDocumentProcessingDataResult,
      ),
      withLatestFrom(this.store.select(AppSelectors.selectPaymentClients)),
      filter(([action, paymentClientsFromStore]) =>
        paymentClientsFromStore.length > 0 || action.type === AppActions.fetchPaymentClients.type,
      ),
      switchMap(() => this.paymentApiService.fetchClients().pipe(
        map(paymentClients => AppActions.fetchPaymentClientsResult({ paymentClients })),
      )),
    ),
  );

  setPaymentDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchInfoPaymentDialogResult),
      withLatestFrom(this.store.select(AppSelectors.selectPaymentDocumentUrl)),
      tap(([, oldUrl]) => {
        if (oldUrl) {
          URL.revokeObjectURL(oldUrl);
        }
      }),
      map(([{ documentType, documentUrl }]) => AppActions.setPaymentDocument({
        documentType: documentType ?? null,
        documentUrl: documentUrl ?? null,
      })),
    ),
  );

  openPaymentDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchInfoPaymentDialogResult),
      withLatestFrom(this.store.select(AppSelectors.selectUserSettings)),
      tap(([
             {
               documentName,
               documentExtension,
               queuedDocuments,
               paymentData,
               globalBeneficiaries,
               clientBeneficiaries,
               reloadAfterSave,
               predictedClient,
             },
             userSettings,
           ]) => {
        if (!userSettings) {
          return;
        }
        const existingBeneficiary = globalBeneficiaries.concat(clientBeneficiaries).find(b => b.counterpartName === paymentData.counterpartName && b.counterpartReference === paymentData.counterpartReference);
        const singleUseBeneficiary: Beneficiary | null = (paymentData.counterpartName || paymentData.counterpartReference)
          ? {
            counterpartName: paymentData.counterpartName,
            counterpartReference: paymentData.counterpartReference,
            singleUse: true,
            id: null,
            global: false,
            type: BeneficiaryType.BENEFICIARY,
          } : null;
        const beneficiary = existingBeneficiary ?? singleUseBeneficiary;
        const paymentFormData: PaymentFormData = {
          clientId: paymentData.clientId || '',
          accountId: paymentData.accountId || '',
          amount: paymentData.amount || null,
          executionDate: paymentData.executionDate || null,
          remittanceInformation: paymentData.remittanceInformation || '',
          internalNote: paymentData.internalNote ?? null,
          remittanceInformationType: paymentData.remittanceInformationType || CreditorRemittanceInformationType.unstructured,
          beneficiary,
          anotherPayment: false,
          editBeneficiary: beneficiary ? toBeneficiaryFormData(beneficiary) : null,
        };
        const request: AddPaymentRequest = {
          paymentFormData,
          reloadAfterSave,
          id: paymentData.id,
          documentId: paymentData.documentId,
          documentName,
          documentExtension,
          clientId: paymentData.clientId,
          queuedDocuments,
          predictedClient,
          userSettings,
        };
        const dialogRef = this.matDialog.openDialogs.length > 0 ? this.matDialog.openDialogs[0] : null;
        this.matDialog.open(PaymentDialogComponent, { disableClose: true, data: request });
        dialogRef?.close();
      }),
    ), { dispatch: false });

  addPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.addPayment),
      concatMap(({ paymentData, anotherPayment, queuedDocuments, reload, context }) =>
        this.paymentApiService.addPayment(paymentData)
          .pipe(
            map(() => AppActions.addPaymentResult({ anotherPayment, queuedDocuments, reload, context })),
            catchError(() => of(AppActions.addPaymentError())),
          ),
      ),
    ),
  );

  updatePayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.updatePayment),
      concatMap(({ paymentData, reload }) =>
        this.paymentApiService.updatePayment(paymentData)
          .pipe(
            map(() => AppActions.updatePaymentResult({ reload })),
            catchError(() => of(AppActions.updatePaymentError())),
          ),
      ),
    ),
  );

  closeUpdatePaymentDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.updatePaymentResult),
      map(() => AppActions.closeDialog({ message: 'PAYMENT_SAVED' })),
    ),
  );

  closeAddPaymentDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.addPaymentResult),
      filter(({ anotherPayment }) => !anotherPayment),
      filter(({ queuedDocuments }) => (queuedDocuments?.length ?? 0) === 0),
      map(() => AppActions.closeDialog({ message: 'PAYMENT_SAVED' })),
    ),
  );

  clearPaymentDocumentAfterAddPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.addPaymentResult),
      filter(({ queuedDocuments }) => (queuedDocuments?.length ?? 0) === 0),
      withLatestFrom(this.store.select(AppSelectors.selectPaymentDocumentUrl)),
      filter(([, url]) => !!url),
      tap(([, url]) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      }),
      map(() => AppActions.setPaymentDocument({ documentType: null, documentUrl: null })),
    ),
  );

  clearPaymentDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.clearPaymentDocument),
      withLatestFrom(this.store.select(AppSelectors.selectPaymentDocumentUrl)),
      filter(([, url]) => !!url),
      tap(([, url]) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      }),
      map(() => AppActions.setPaymentDocument({ documentType: null, documentUrl: null })),
    ),
  );

  addAnotherPaymentConfirmed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.addPaymentResult),
      filter(({ anotherPayment }) => anotherPayment),
      filter(({ queuedDocuments }) => (queuedDocuments?.length ?? 0) === 0),
      tap(() => this.toastService.show(Toast.Success, 'PAYMENT_SAVED')),
    ), { dispatch: false },
  );

  checkDuplicatePayments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.checkDuplicatePayments),
      switchMap(({ clientId, request }) =>
        this.paymentApiService.findDuplicatePayments(clientId, request).pipe(
          map((duplicates) => {
            const latestExecutionDate = this.latestDate(duplicates.map(duplicate => duplicate.executionDate));
            const latestProcessedAt = this.latestDate(duplicates.map(duplicate => duplicate.processedAt));
            const duplicatePaymentsType = duplicates.length === 0
              ? null
              : (latestExecutionDate ? 'executed' : (latestProcessedAt ? 'submitted' : 'queued'));
            const duplicatePaymentsLatestDate = latestExecutionDate ?? latestProcessedAt;
            return AppActions.checkDuplicatePaymentsResult({ duplicatePaymentsType, duplicatePaymentsLatestDate });
          }),
          catchError(() => of(AppActions.checkDuplicatePaymentsError())),
        ),
      ),
    ),
  );
  nextQueuedDocumentAfterAddPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.addPaymentResult),
      filter(({ queuedDocuments }) => (queuedDocuments?.length ?? 0) > 0),
      tap(() => this.toastService.show(Toast.Success, 'PAYMENT_SAVED')),
      map(({ queuedDocuments, reload, context }) => {
        const qDocs = [...queuedDocuments!];
        const document = qDocs.shift()!;
        const documentId = document.id;
        const documentType = payDocumentType(document);
        if (context === 'payment' && !!documentType) {
          return AppActions.fetchDocumentPaymentData({
            clientId: document.client?.id,
            documentId,
            documentType,
            queuedDocuments: qDocs,
            reloadAfterSave: reload,
          });
        }
        if (context === 'process') {
          return AppActions.fetchDocumentProcessingData({
            document,
            documentType,
            queuedDocuments: qDocs,
          });
        }
        return AppActions.closeDialog({});
      }),
    ),
  );
  fetchClientBeneficiaries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchClientBeneficiaries),
      switchMap(({ clientId }) => {
          if (clientId !== '') {
            return this.paymentApiService.fetchClientBeneficiaries(clientId)
              .pipe(
                map((beneficiaries) => AppActions.fetchClientBeneficiariesResult({ beneficiaries })),
                catchError(() => of(AppActions.fetchClientBeneficiariesError())),
              );
          } else {
            return of(AppActions.fetchClientBeneficiariesResult({ beneficiaries: [] }));
          }
        },
      ),
    ),
  );
  reloadAfterAddPayment$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppActions.addPaymentResult),
        filter(({ reload }) => reload),
        filter(({ queuedDocuments }) => (queuedDocuments?.length ?? 0) === 0),
        tap(() =>
          this.router.navigate(
            [],
            {
              relativeTo: this.route,
              replaceUrl: true,
              onSameUrlNavigation: 'reload',
              queryParamsHandling: 'merge',
            },
          ),
        ),
      ),
    { dispatch: false },
  );
  reloadAfterUpdatePayment$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppActions.updatePaymentResult),
        filter(({ reload }) => reload),
        tap(() =>
          this.router.navigate(
            [],
            {
              relativeTo: this.route,
              replaceUrl: true,
              onSameUrlNavigation: 'reload',
              queryParamsHandling: 'merge',
            },
          ),
        ),
      ),
    { dispatch: false },
  );
  updateBeneficiary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.updateBeneficiary),
      withLatestFrom(this.store.select(AppSelectors.selectAccountUser).pipe(filter(Boolean))),
      concatMap(([{ clientId, beneficiaryId, beneficiary }, user]) =>
        this.paymentApiService.updateBeneficiary(beneficiaryId, clientId, beneficiary)
          .pipe(
            switchMap(() => forkJoin([
              this.paymentApiService.fetchGlobalBeneficiaries(user.officeId),
              this.paymentApiService.fetchClientBeneficiaries(clientId),
            ]).pipe(
              map(([globalBeneficiaries, clientBeneficiaries]) => AppActions.editBeneficiariesResult({
                globalBeneficiaries,
                clientBeneficiaries,
              })),
            )),
            catchError(() => of(AppActions.editBeneficiariesError())),
          ),
      ),
    ),
  );
  addBeneficiary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.addBeneficiary),
      withLatestFrom(this.store.select(AppSelectors.selectAccountUser).pipe(filter(Boolean))),
      concatMap(([{ clientId, beneficiary }, user]) =>
        this.paymentApiService.addBeneficiary(clientId, beneficiary)
          .pipe(
            switchMap(() => forkJoin([
              this.paymentApiService.fetchGlobalBeneficiaries(user.officeId),
              this.paymentApiService.fetchClientBeneficiaries(clientId),
            ]).pipe(
              map(([globalBeneficiaries, clientBeneficiaries]) => AppActions.editBeneficiariesResult({
                globalBeneficiaries,
                clientBeneficiaries,
              })),
            )),
            catchError(() => of(AppActions.editBeneficiariesError())),
          ),
      ),
    ),
  );
  deleteBeneficiary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.deleteBeneficiary),
      withLatestFrom(this.store.select(AppSelectors.selectAccountUser).pipe(filter(Boolean))),
      concatMap(([{ clientId, beneficiaryId }, user]) =>
        this.paymentApiService.deleteBeneficiary(beneficiaryId, clientId)
          .pipe(
            switchMap(() => forkJoin([
              this.paymentApiService.fetchGlobalBeneficiaries(user.officeId),
              this.paymentApiService.fetchClientBeneficiaries(clientId),
            ]).pipe(
              map(([globalBeneficiaries, clientBeneficiaries]) => AppActions.editBeneficiariesResult({
                globalBeneficiaries,
                clientBeneficiaries,
              })),
            )),
            catchError(() => of(AppActions.editBeneficiariesError())),
          ),
      ),
    ),
  );
  fetchViewDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchViewDocument),
      switchMap(({ document }) => {
        const documentType = displayDocumentType(document);
        return documentType
          ? this.documentApiService.downloadDocument(document.id, document.client?.id)
            .pipe(
              map((blob) => AppActions.fetchViewDocumentResult({
                document,
                documentType,
                documentUrl: URL.createObjectURL(blob),
              })),
              catchError(() => of(AppActions.fetchViewDocumentError())),
            )
          : of(AppActions.fetchViewDocumentResult({ document, documentType: null, documentUrl: null }));
      }),
    ),
  );
  openViewDocumentDialog$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppActions.fetchViewDocumentResult),
        withLatestFrom(this.store.select(AppSelectors.selectUserSettings)),
        tap(([{ document }, userSettings]) => {
          if (!userSettings) {
            return;
          }
          const data: ViewDocumentRequest = { document, userSettings };
          this.matDialog.open(ViewDocumentDialogComponent, {
            disableClose: true, data,
          });
        }),
      ),
    { dispatch: false },
  );
  setViewDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchViewDocumentResult),
      withLatestFrom(this.store.select(AppSelectors.selectViewDocumentUrl)),
      tap(([, oldUrl]) => {
        if (oldUrl) {
          URL.revokeObjectURL(oldUrl);
        }
      }),
      map(([{ documentType, documentUrl }]) => AppActions.setViewDocument({
        documentType,
        documentUrl,
      })),
    ),
  );
  clearViewDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.clearViewDocument),
      withLatestFrom(this.store.select(AppSelectors.selectViewDocumentUrl)),
      filter(([, url]) => !!url),
      tap(([, url]) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      }),
      map(() => AppActions.setViewDocument({ documentType: null, documentUrl: null })),
    ),
  );
  fetchDocumentAssignmentData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchDocumentAssignmentData),
      switchMap(({ documents }) => {
        const document = documents.length === 1 ? documents[0] : null;
        const documentType = document ? displayDocumentType(document) : null;
        if (document) {
          return forkJoin([
            this.documentApiService.predictClient(document.id)
              .pipe(catchError(() => of({ predictedClient: undefined }))),
            documentType ? this.documentApiService.downloadDocument(document.id, document.client?.id) : of(undefined),
          ]).pipe(
            map(([predictedClientResponse, blob]) => AppActions.fetchDocumentAssignmentDataResult({
              documents,
              documentType,
              documentUrl: blob ? URL.createObjectURL(blob) : null,
              predictedClientId: predictedClientResponse.predictedClient?.id,
            })),
            catchError(() => of(AppActions.fetchDocumentAssignmentDataError())),
          );
        } else {
          return of(AppActions.fetchDocumentAssignmentDataResult({
            documents,
            documentType: null,
            documentUrl: null,
          }));
        }
      }),
    ),
  );
  fetchInfoAssignDocumentDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchDocumentAssignmentDataResult),
      withLatestFrom(this.store.select(AppSelectors.selectAssignDocumentClients)),
      switchMap(([{ documents, documentType, documentUrl, predictedClientId }, clientsFromStore]) =>
        (
          clientsFromStore.length > 0
            ? of<SimpleClient[] | undefined>(undefined)
            : this.documentApiService.fetchClients()
        ).pipe(
          map((clients) =>
            AppActions.fetchInfoAssignDocumentDialogResult({
              documents,
              documentType,
              documentUrl,
              clients,
              predictedClientId,
            }),
          ),
          catchError(() => of(AppActions.fetchInfoAssignDocumentDialogError())),
        ),
      ),
    ),
  );
  fetchAssignDocumentClients$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchDocumentAssignmentDataResult, AppActions.fetchDocumentProcessingDataResult),
      withLatestFrom(this.store.select(AppSelectors.selectAssignDocumentClients)),
      filter(([, assignDocumentClientsFromStore]) => assignDocumentClientsFromStore.length > 0),
      switchMap(() => this.documentApiService.fetchClients().pipe(
        map(clients => AppActions.fetchAssignDocumentClientsResult({ clients })),
      )),
    ),
  );
  setAssignDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchInfoAssignDocumentDialogResult),
      withLatestFrom(this.store.select(AppSelectors.selectAssignDocumentUrl)),
      tap(([, oldUrl]) => {
        if (oldUrl) {
          URL.revokeObjectURL(oldUrl);
        }
      }),
      map(([{ documentType, documentUrl }]) => AppActions.setAssignDocument({
        documentType,
        documentUrl,
      })),
    ),
  );
  openAssignDocumentDialog$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppActions.fetchInfoAssignDocumentDialogResult),
        withLatestFrom(this.store.select(AppSelectors.selectUserSettings)),
        tap(([{ documents, predictedClientId }, userSettings]) => {
          if (!userSettings) {
            return;
          }
          const data: AssignDocumentRequest = { documents, predictedClientId, userSettings };
          const dialogRef = this.matDialog.openDialogs.length > 0 ? this.matDialog.openDialogs[0] : null;
          this.matDialog.open(AssignDocumentDialogComponent, {
            disableClose: true, data,
          });
          dialogRef?.close();
        }),
      ),
    { dispatch: false },
  );
  assignDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.assignDocument),
      concatMap(({ documentIds, clientId, queuedDocuments }) =>
        this.documentApiService.assignDocuments(documentIds, clientId).pipe(
          map(() => AppActions.assignDocumentSuccess({ queuedDocuments })),
          catchError(() => of(AppActions.assignDocumentError())),
        ),
      ),
    ),
  );
  closeAssignDocumentDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.assignDocumentSuccess),
      filter(({ queuedDocuments }) => (queuedDocuments?.length ?? 0) === 0),
      map(() => AppActions.closeDialog({ message: 'DOCUMENT_ASSIGNED' })),
    ),
  );
  nextQueuedDocumentAfterAssignDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.assignDocumentSuccess),
      filter(({ queuedDocuments }) => (queuedDocuments?.length ?? 0) > 0),
      tap(() => this.toastService.show(Toast.Success, 'DOCUMENT_ASSIGNED')),
      map(({ queuedDocuments }) => {
        const qDocs = [...queuedDocuments!];
        const document = qDocs.shift()!;
        const documentType = payDocumentType(document);
        return AppActions.fetchDocumentProcessingData({
          document,
          documentType,
          queuedDocuments: qDocs,
        });
      }),
    ),
  );
  clearAssignDocumentAfterAssignDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.assignDocumentSuccess),
      withLatestFrom(this.store.select(AppSelectors.selectAssignDocumentUrl)),
      filter(([, url]) => !!url),
      tap(([, url]) => {
        if (url) {
          URL.revokeObjectURL(url as string);
        }
      }),
      map(() => AppActions.clearAssignDocument()),
    ),
  );
  clearAssignDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.clearAssignDocument),
      withLatestFrom(this.store.select(AppSelectors.selectAssignDocumentUrl)),
      filter(([, url]) => !!url),
      tap(([, url]) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      }),
      map(() => AppActions.setAssignDocument({ documentType: null, documentUrl: null })),
    ),
  );
  fetchDocumentProcessingData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchDocumentProcessingData),
      withLatestFrom(this.featureService.officeHasFeature(Feature.ISABEL_PI)),
      switchMap(([{ document, documentType, queuedDocuments }, paymentFeatureEnabled]) => {
          if (documentType) {
            return forkJoin([
              paymentFeatureEnabled ? this.documentApiService.getDocumentPaymentData(document.id) : of(undefined),
              this.documentApiService.downloadDocument(document.id),
            ]).pipe(
              map(([paymentData, blob]) => AppActions.fetchDocumentProcessingDataResult({
                document,
                documentType,
                documentUrl: URL.createObjectURL(blob),
                queuedDocuments,
                paymentData: paymentData ? { ...paymentData, documentId: document.id } : undefined,
              })),
              catchError(() => of(AppActions.fetchDocumentProcessingDataError())),
            );
          } else {
            return of(AppActions.fetchDocumentProcessingDataResult({
              document,
              documentType: null,
              documentUrl: null,
              queuedDocuments,
            }));
          }
        },
      ),
    ),
  );
  fetchInfoProcessDocumentDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchDocumentProcessingDataResult),
      withLatestFrom(
        this.store.select(AppSelectors.selectPaymentAccounts),
        this.store.select(AppSelectors.selectPaymentClients),
        this.store.select(AppSelectors.selectAssignDocumentClients),
        this.store.select(AppSelectors.selectAccountUser).pipe(filter(Boolean)),
      ),
      switchMap(([
                   { document, documentType, documentUrl, queuedDocuments, paymentData },
                   paymentAccounts, paymentClients, assignDocumentClients, user,
                 ]) => forkJoin(([
          paymentAccounts.length > 0 ? of(undefined) : this.paymentApiService.fetchAccounts(),
          paymentClients.length > 0 ? of(undefined) : this.paymentApiService.fetchClients(),
          this.paymentApiService.fetchGlobalBeneficiaries(user.officeId),
          assignDocumentClients.length > 0 ? of(undefined) : this.documentApiService.fetchClients(),
          this.documentApiService.predictClient(document.id).pipe((catchError(() => of({} as PredictClientResponse)))),
        ])).pipe(
          switchMap(([paymentAccounts, paymentClients, globalBeneficiaries, assignDocumentClients, predictedClientResponse]) => {
            return (
              predictedClientResponse.predictedClient
                ? this.paymentApiService.fetchClientBeneficiaries(predictedClientResponse.predictedClient.id)
                : of([] as Beneficiary[])
            ).pipe(map((clientBeneficiaries) => {
              return AppActions.fetchInfoProcessDocumentDialogResult({
                document,
                documentType,
                documentUrl,
                queuedDocuments,
                paymentData,
                paymentAccounts,
                paymentClients,
                globalBeneficiaries,
                clientBeneficiaries,
                assignDocumentClients,
                predictedClient: predictedClientResponse.predictedClient,
              });
            }));
          }),
          catchError(() => of(AppActions.fetchInfoProcessDocumentDialogError())),
        ),
      ),
    ),
  );
  setProcessDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchInfoProcessDocumentDialogResult),
      withLatestFrom(this.store.select(AppSelectors.selectProcessDocumentUrl)),
      tap(([, oldUrl]) => {
        if (oldUrl) {
          URL.revokeObjectURL(oldUrl);
        }
      }),
      map(([{ documentType, documentUrl }]) => AppActions.setProcessDocument({
        documentType,
        documentUrl,
      })),
    ),
  );
  openProcessDocumentDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchInfoProcessDocumentDialogResult),
      withLatestFrom(this.store.select(AppSelectors.selectUserSettings)),
      tap(([{ document, queuedDocuments, paymentData, predictedClient }, userSettings]) => {
        if (!userSettings) {
          return;
        }
        const request: ProcessDocumentRequest = {
          document,
          queuedDocuments,
          predictedClient,
          userSettings,
        };
        if (paymentData) {
          const beneficiary: Beneficiary | null = (paymentData.counterpartName || paymentData.counterpartReference)
            ? {
              counterpartName: paymentData.counterpartName,
              counterpartReference: paymentData.counterpartReference,
              singleUse: true,
              id: null,
              global: false,
              type: BeneficiaryType.BENEFICIARY,
            } : null;
          request.paymentFormData = {
            clientId: paymentData.clientId || '',
            accountId: paymentData.accountId || '',
            amount: paymentData.amount || null,
            executionDate: paymentData.executionDate || null,
            remittanceInformation: paymentData.remittanceInformation || '',
            internalNote: paymentData.internalNote ?? null,
            remittanceInformationType: paymentData.remittanceInformationType || CreditorRemittanceInformationType.unstructured,
            beneficiary,
            anotherPayment: false,
            editBeneficiary: beneficiary ? toBeneficiaryFormData(beneficiary) : null,
          };
        }
        const dialogRef = this.matDialog.openDialogs.length > 0 ? this.matDialog.openDialogs[0] : null;
        this.matDialog.open(ProcessDocumentDialogComponent, { disableClose: true, data: request });
        dialogRef?.close();
      }),
    ), { dispatch: false });
  fetchInfoXs2aDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchInfoXs2aDialog),
      switchMap(({ financialInstitutionId, importIban }) => {
          if (financialInstitutionId) {
            return this.xs2aApiService.fetchFinancialInstitution(financialInstitutionId).pipe(
              map((financialInstitution) =>
                AppActions.fetchInfoXs2aDialogResult({
                  financialInstitutionId,
                  financialInstitutions: [financialInstitution],
                  importIban,
                })),
              catchError(() => of(AppActions.fetchInfoXs2aDialogError())),
            );
          } else {
            return this.xs2aApiService.fetchFinancialInstitutions().pipe(
              map((financialInstitutions) =>
                AppActions.fetchInfoXs2aDialogResult({
                  financialInstitutions,
                  importIban,
                }),
              ),
              catchError(() => of(AppActions.fetchInfoXs2aDialogError())),
            );
          }
        },
      ),
    ),
  );
  openXs2aDialog$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppActions.fetchInfoXs2aDialogResult),
        tap(({ financialInstitutionId, importIban }) =>
          this.matDialog.open(Xs2aDialogComponent, {
            disableClose: true,
            data: { financialInstitutionId, importIban },
          }),
        ),
      ),
    { dispatch: false },
  );
  fetchAiarInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchAiarInfo),
      switchMap(({ financialInstitutionId, forImport }) =>
        this.xs2aApiService.fetchAiarInfo(financialInstitutionId, forImport).pipe(
          map((aiarInfo) => AppActions.fetchAiarInfoResult({ aiarInfo })),
          catchError(() => of(AppActions.fetchAiarInfoError())),
        ),
      ),
    ),
  );
  createAiar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.createAiar),
      concatMap(({ financialInstitutionId, forImport, customerReference, ibans }) =>
        this.xs2aApiService.createAiar(financialInstitutionId, forImport, customerReference, ibans).pipe(
          map((redirectUri) => AppActions.createAiarResult({ redirectUri })),
          catchError(() => of(AppActions.createAiarError())),
        ),
      ),
    ),
  );
  redirectToFinancialInstitution$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.createAiarResult),
      tap(({ redirectUri }) => {
        this.document.location = redirectUri;
      }),
      map(() => AppActions.closeDialog({ message: 'REDIRECTING_TO_FINANCIAL_INSTITUTION' })),
    ),
  );
  processRedirectFromFinancialInstitution$ = createEffect(() =>
      this.actions$.pipe(
        ofType(routerNavigatedAction),
        map(action => action.payload.routerState),
        filter(({ url }) => url.matchesUrl(AppRoute.Xs2aRedirect.split('/'))),
        tap(({ root: { queryParams } }) =>
          this.matDialog.open(Xs2aRedirectDialogComponent, {
            disableClose: true,
            data: { queryParams },
          }),
        ),
      ),
    { dispatch: false },
  );
  createAiarAuthorization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.createAiarAuthorization),
      concatMap(({ queryParams }) =>
        this.xs2aApiService.createAiarAuthorization(queryParams).pipe(
          map((aiarAuthorization) => AppActions.createAiarAuthorizationResult({ aiarAuthorization })),
          catchError(() => of(AppActions.createAiarAuthorizationError())),
        ),
      ),
    ),
  );
  closeXs2aRedirectDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.closeXs2aRedirectDialog),
      tap(() => this.router.navigateByUrl(AppRoute.BankAccounts)),
      map(() => AppActions.closeDialog({})),
    ),
  );
  processRedirectFromIsabel$ = createEffect(() =>
      this.actions$.pipe(
        ofType(routerNavigatedAction),
        map(action => action.payload.routerState),
        filter(({ url }) => url.matchesUrl(AppRoute.IsabelRedirect.split('/'))),
        tap(({ root: { queryParams } }) =>
          this.matDialog.open(IsabelRedirectDialogComponent, {
            disableClose: true,
            data: { queryParams },
          }),
        ),
      ),
    { dispatch: false },
  );
  createIsabelAuthorization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.createIsabelAuthorization),
      concatMap(({ code }) =>
        this.isabelApiService.createIsabelAuthorization(code).pipe(
          map(() => AppActions.createIsabelAuthorizationSuccess()),
          catchError(() => of(AppActions.createIsabelAuthorizationError())),
        ),
      ),
    ),
  );
  closeIsabelRedirectDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.closeIsabelRedirectDialog),
      tap(() => this.router.navigateByUrl(AppRoute.Settings + '/isabel-connect')),
      map(() => AppActions.closeDialog({})),
    ),
  );
  reloadPage$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppActions.reloadPage),
        tap(() =>
          this.router.navigate(
            [],
            {
              relativeTo: this.route,
              replaceUrl: true,
              onSameUrlNavigation: 'reload',
              queryParamsHandling: 'merge',
            },
          ),
        ),
      ),
    { dispatch: false },
  );

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(ENVIRONMENT) private readonly environment: Environment,
    @Inject(LOCAL_STORAGE) private readonly localStorage: Storage,
    private readonly authService: AuthService,
    private readonly actions$: Actions,
    private readonly addClientApiService: AddClientApiService,
    private readonly counterApiService: CounterApiService,
    private readonly documentApiService: DocumentApiService,
    private readonly featureFlagApiService: FeatureFlagApiService,
    private readonly featureService: FeatureService,
    private readonly maintenanceModeApiService: MaintenanceModeApiService,
    private readonly matDialog: MatDialog,
    private readonly officeApiService: OfficeApiService,
    private readonly paymentApiService: PaymentApiService,
    private readonly rbAuthService: RbAuthService,
    private readonly respondToDocumentApiService: RespondToDocumentApiService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly userApiService: UserApiService,
    private readonly xs2aApiService: Xs2aApiService,
    private readonly isabelApiService: IsabelApiService,
  ) {
  }

  private latestDate(dates: (Date | undefined)[]) {
    return dates
      .filter((date): date is Date => date != null)
      .reduce<Date | null>((latest, current) => {
        if (latest == null || current > latest) {
          return current;
        }
        return latest;
      }, null);
  }
}
